import React, { useState } from 'react';
import { Upload, Lock, Image as ImageIcon, RotateCcw, Info } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { Card, Toggle, FieldLabel, inputClass, SegmentedControl } from './walletUi';
import { WalletPassPreview, WalletPlatform } from './WalletPassPreview';
import { walletTiers as initialTiers, WalletTierDesign } from '../../data/mockDigitalWallet';
function ColourField({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return <div data-id="element-2365" data-mp="e1cuf7wxq99o">
      <FieldLabel data-id="element-2366" data-mp="e13c86i4t6ej">{label}</FieldLabel>
      <div className="flex items-center gap-2 h-10 px-2 border border-[#CBD5E1] rounded-md bg-white" data-id="element-2367" data-mp="2deqpreh4pn">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-7 h-7 rounded border-0 p-0 bg-transparent cursor-pointer" data-id="element-2368" data-mp="435r3rvkjpq5" />
        <input value={value.toUpperCase()} onChange={e => onChange(e.target.value)} className="flex-1 text-sm text-[#0F172A] focus:outline-none font-mono" data-id="element-2369" data-mp="2kr16u11q6fks" />
      </div>
    </div>;
}
function UploadRow({
  label,
  hint,
  fileName
}: {
  label: string;
  hint: string;
  fileName: string;
}) {
  return <div data-id="element-2370" data-mp="3li8p9ala1n3t">
      <FieldLabel hint={hint} data-id="element-2371" data-mp="3liiegvyhzq8g">{label}</FieldLabel>
      <div className="flex items-center gap-3 p-3 border border-dashed border-[#CBD5E1] rounded-md bg-[#F8FAFC]" data-id="element-2372" data-mp="3b1cqxy7dtyd">
        <div className="w-9 h-9 rounded bg-white border border-[#E2E8F0] flex items-center justify-center" data-id="element-2373" data-mp="3vdsl5wzuclsk">
          <ImageIcon className="w-4 h-4 text-[#94A3B8]" data-id="element-2374" data-mp="d7e8s2h80jwa" />
        </div>
        <span className="text-sm text-[#0F172A] flex-1 truncate" data-id="element-2375" data-mp="8anrjbtoji9a">{fileName}</span>
        <Button variant="secondary" size="sm" iconLeft={<Upload className="w-4 h-4" data-id="element-2377" data-mp="my8riwv9d1cz" />} data-id="element-2376" data-mp="mza6hcfea7zz">
          Replace
        </Button>
      </div>
    </div>;
}
const lockedFront = [['Header', 'Tier, Points'], ['Below banner', 'Full name, Next tier, Rewards (count available)'], ['Barcode', 'QR code, member ID']];
const lockedBack = ['Your benefits', 'Rewards breakdown', 'Member since', 'Club', 'Notifications and history', 'View my rewards link', 'Shop link'];
export function WalletDesignTab({
  onSaved
}: {
  onSaved: (msg: string) => void;
}) {
  const [tiers, setTiers] = useState<WalletTierDesign[]>(initialTiers);
  const [activeId, setActiveId] = useState(initialTiers[1].id);
  const [sameForAll, setSameForAll] = useState(false);
  const [platform, setPlatform] = useState<WalletPlatform>('apple');
  const [side, setSide] = useState<'front' | 'back'>('front');
  const [dirty, setDirty] = useState(false);
  const [rewardsUrl, setRewardsUrl] = useState('https://acmeestate.com.au/apps/members/rewards');
  const [shopUrl, setShopUrl] = useState('https://acmeestate.com.au/collections/all');
  const active = sameForAll ? tiers[0] : tiers.find(t => t.id === activeId)!;
  const update = (patch: Partial<WalletTierDesign>) => {
    setDirty(true);
    setTiers(prev => prev.map(t => sameForAll || t.id === active.id ? {
      ...t,
      ...patch
    } : t));
  };
  return <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 items-start" data-id="element-2378" data-mp="2n6ekl0b3dzi5">
      <div className="space-y-6" data-id="element-2379" data-mp="2fmdflw8zo32t">
        <Card title="Tier designs" description="Each tier can have its own colours and banner, so the pass changes as members move up." action={<div className="flex items-center gap-2 text-sm text-[#475569]" data-id="element-2381" data-mp="25wam7uxygn3y">
              <span data-id="element-2382" data-mp="21u77mxfzp2eg">Same design for all tiers</span>
              <Toggle on={sameForAll} onChange={v => {
          setSameForAll(v);
          setDirty(true);
        }} label="Same design for all tiers" data-id="element-2383" data-mp="21vi9ustse23g" />
            </div>} data-id="element-2380" data-mp="2aulwu7w2r1n8">
          {!sameForAll && <div className="flex flex-wrap gap-2 mb-5" data-id="element-2384" data-mp="2tld5t8c5gq13">
              {tiers.map(t => <button key={t.id} type="button" onClick={() => setActiveId(t.id)} className={`flex items-center gap-2 px-3 h-9 rounded-md border text-sm font-medium transition-colors ${t.id === activeId ? 'border-[#0EA5E9] bg-[#F0F9FF] text-[#0369A1]' : 'border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-2385" data-mp="2pi6zk0p1eyum">
                  <span className="w-3.5 h-3.5 rounded-sm border border-black/10" style={{
              background: t.background
            }} data-id="element-2386" data-mp="19473x1x85vd6" />
                  {t.name}
                </button>)}
            </div>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-id="element-2387" data-mp="31tpkix176ew3">
            <ColourField label="Background" value={active.background} onChange={v => update({
            background: v
          })} data-id="element-2388" data-mp="2o2196vyrvug6" />
            <ColourField label="Text" value={active.text} onChange={v => update({
            text: v
          })} data-id="element-2389" data-mp="2s0ij1n9kvj1a" />
            <ColourField label="Labels" value={active.label} onChange={v => update({
            label: v
          })} data-id="element-2390" data-mp="3kraa66bsdhhr" />
          </div>

          <UploadRow label={sameForAll ? 'Banner image' : `Banner image for ${active.name}`} hint="One image, cropped for Apple (1125 × 432) and Google (1032 × 336). JPG or PNG." fileName={active.bannerFileName} data-id="element-2391" data-mp="36qg07j93b8gz" />

          <div className="mt-3 grid grid-cols-2 gap-3" data-id="element-2392" data-mp="1vbjb69cwoj9m">
            <div data-id="element-2393" data-mp="20b5oeisqhzif">
              <div className="text-[11px] text-[#94A3B8] mb-1" data-id="element-2394" data-mp="20bp4dtytqdtt">Apple crop</div>
              <div className="h-14 rounded" style={{
              background: active.banner
            }} data-id="element-2395" data-mp="1ru515f2rsnvp" />
            </div>
            <div data-id="element-2396" data-mp="1nrrveqo0wpyx">
              <div className="text-[11px] text-[#94A3B8] mb-1" data-id="element-2397" data-mp="1ns9z28d0m6gv">Google crop</div>
              <div className="h-12 rounded" style={{
              background: active.banner
            }} data-id="element-2398" data-mp="21rjh7dks7anh" />
            </div>
          </div>
        </Card>

        <Card title="Logo and icon" description="Used on every tier." data-id="element-2399" data-mp="193x1j6ya4fk9">
          <div className="space-y-4" data-id="element-2400" data-mp="1946r4tfewkpf">
            <UploadRow label="Logo" hint="Square, at least 300 × 300. Shown top left of the pass." fileName="acme-estate-logo.png" data-id="element-2401" data-mp="1453zcaog0aze" />
            <UploadRow label="Notification icon" hint="Square with a solid background. Shown next to pushes on the lock screen." fileName="acme-estate-icon.png" data-id="element-2402" data-mp="1iurwr0ti6phu" />
          </div>
        </Card>

        <Card title="Pass links" description="Shown on the back of the pass, and as the action buttons on newer iPhones." data-id="element-2403" data-mp="qcdzb2c2x5ja">
          <div className="space-y-4" data-id="element-2404" data-mp="ldt6vp32jq5e">
            <div data-id="element-2405" data-mp="2lgluih8mfuwk">
              <FieldLabel hint="Defaults to your member portal rewards page." data-id="element-2406" data-mp="2lh3thlmo4n86">View my rewards</FieldLabel>
              <input className={inputClass} value={rewardsUrl} onChange={e => {
              setRewardsUrl(e.target.value);
              setDirty(true);
            }} data-id="element-2407" data-mp="2qevm6hv50ddf" />
            </div>
            <div data-id="element-2408" data-mp="2z8ie1ww84whc">
              <FieldLabel hint="Defaults to your online store." data-id="element-2409" data-mp="2z6xm8f1anrn6">Shop</FieldLabel>
              <input className={inputClass} value={shopUrl} onChange={e => {
              setShopUrl(e.target.value);
              setDirty(true);
            }} data-id="element-2410" data-mp="22i9f4dr49osj" />
            </div>
          </div>
        </Card>

        <Card title="What the pass shows" description="Set by WinePik and kept up to date automatically. These can't be changed." action={<Lock className="w-4 h-4 text-[#94A3B8]" data-id="element-2412" data-mp="2gisn5kvl0uup" />} data-id="element-2411" data-mp="2bhd1bqlkykb9">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2413" data-mp="3my31arzeufwf">
            <div data-id="element-2414" data-mp="3mzxko1v19zye">
              <div className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2" data-id="element-2415" data-mp="3rwebx4xj4ufp">Front</div>
              <dl className="space-y-2 m-0" data-id="element-2416" data-mp="5vqd89qppjs0">
                {lockedFront.map(([k, v]) => <div key={k} className="text-sm" data-id="element-2417" data-mp="w42c6ycwfmd">
                    <dt className="text-[#94A3B8] text-xs" data-id="element-2418" data-mp="xf3dz0f399s">{k}</dt>
                    <dd className="m-0 text-[#0F172A]" data-id="element-2419" data-mp="3sa9s07jln0z0">{v}</dd>
                  </div>)}
              </dl>
            </div>
            <div data-id="element-2420" data-mp="3o853tobx6wjr">
              <div className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-2" data-id="element-2421" data-mp="3o8ojszi0fav5">Back</div>
              <ul className="m-0 pl-0 list-none space-y-1" data-id="element-2422" data-mp="3jbeoj37xw4ze">
                {lockedBack.map(f => <li key={f} className="text-sm text-[#0F172A]" data-id="element-2423" data-mp="3ea8un22jmboa">{f}</li>)}
              </ul>
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between bg-white border border-[#E2E8F0] rounded-lg px-6 py-4 shadow-sm" data-id="element-2424" data-mp="39dqp3zze29aw">
          <div className="flex items-center gap-2 text-sm text-[#64748B]" data-id="element-2425" data-mp="17l2lhpiu1jul">
            <Info className="w-4 h-4" data-id="element-2426" data-mp="17i7xjo0m1gwf" />
            Saving updates every member's pass. Changes appear in their wallet within a few minutes.
          </div>
          <div className="flex gap-2" data-id="element-2427" data-mp="1mt0csrz5obx5">
            <Button variant="ghost" size="md" iconLeft={<RotateCcw className="w-4 h-4" data-id="element-2429" data-mp="1qs965vqaa7s6" />} disabled={!dirty} onClick={() => {
            setTiers(initialTiers);
            setDirty(false);
          }} data-id="element-2428" data-mp="1mu3594c4mmf6">
              Discard
            </Button>
            <Button variant="primary" size="md" disabled={!dirty} onClick={() => {
            setDirty(false);
            onSaved('Pass design saved. Members will see the update shortly.');
          }} data-id="element-2430" data-mp="1hzyxiiv1ead1">
              Save design
            </Button>
          </div>
        </div>
      </div>

      <div className="xl:sticky xl:top-6" data-id="element-2431" data-mp="u437rqjygbmc">
        <Card title="Preview" description={sameForAll ? 'All tiers' : `${active.name} tier`} data-id="element-2432" data-mp="u54lk2scvc7r">
          <div className="flex items-center justify-between mb-4" data-id="element-2433" data-mp="z48r8jg2mrtc">
            <SegmentedControl value={platform} onChange={setPlatform} options={[{
            value: 'apple',
            label: 'Apple'
          }, {
            value: 'google',
            label: 'Google'
          }]} data-id="element-2434" data-mp="2oidx004gp7sn" />
            <SegmentedControl value={side} onChange={setSide} options={[{
            value: 'front',
            label: 'Front'
          }, {
            value: 'back',
            label: 'Back'
          }]} data-id="element-2435" data-mp="33kca3mmljfom" />
          </div>
          <div className="flex justify-center bg-[#F8FAFC] rounded-lg py-6" data-id="element-2436" data-mp="37lpju7st830t">
            <WalletPassPreview tier={active} platform={platform} side={side} data-id="element-2437" data-mp="37jl5evxablma" />
          </div>
          {platform === 'apple' && side === 'front' && <p className="text-xs text-[#94A3B8] mt-3 m-0" data-id="element-2438" data-mp="3hc0ndt8l0a76">
              Button text and icons are set by Apple. Shown on iPhones with iOS 27 or later.
            </p>}
        </Card>
      </div>
    </div>;
}